import { useEffect, useMemo, useState } from 'react';
import {
  ListPlus,
  RefreshCcw,
  Save,
  Stethoscope,
  Trash2,
} from 'lucide-react';
import { toast } from 'react-toastify';
import TenantSiteImageUploadField from '../../components/admin/TenantSiteImageUploadField';
import {
  defaultServicesPageContent,
  mergeServicesPageContent,
} from '../../content/defaultTenantSitePages';
import { useAuth } from '../../context/AuthContext';
import { tenantSiteImageAssetConfigs } from '../../services/TenantSiteImageStorageService';
import { TenantSitePagesService } from '../../services/TenantSitePagesService';
import type {
  TenantServicesPageContent,
  TenantSiteAsset,
  TenantSiteFeatureItem,
} from '../../types/site-pages';

const iconOptions = [
  { value: 'stethoscope', label: 'Stethoscope' },
  { value: 'dumbbell', label: 'Dumbbell' },
  { value: 'heart', label: 'Heart' },
  { value: 'activity', label: 'Activity' },
  { value: 'home', label: 'Home' },
  { value: 'microscope', label: 'Microscope' },
] as const;

const createEmptyAsset = (): TenantSiteAsset => ({
  provider: 'firebase-storage',
  storagePath: '',
  downloadUrl: '',
  alt: '',
});

const createServiceItem = (): TenantSiteFeatureItem => ({
  id: `service-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  iconKey: 'stethoscope',
  title: '',
  description: '',
});

const cloneServicesContent = (
  content: TenantServicesPageContent,
): TenantServicesPageContent => JSON.parse(JSON.stringify(content)) as TenantServicesPageContent;

const ServicesPageEditor = () => {
  const { user, selectedClient } = useAuth();
  const clientId = selectedClient?.clientId ?? '';
  const [formData, setFormData] = useState<TenantServicesPageContent>(() =>
    cloneServicesContent(defaultServicesPageContent),
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSavedOverride, setHasSavedOverride] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);

  const serviceItems = useMemo(
    () => formData.services?.items ?? [],
    [formData.services?.items],
  );

  const loadContent = async () => {
    if (!clientId) {
      setFormData(cloneServicesContent(defaultServicesPageContent));
      setHasSavedOverride(false);
      setLastUpdatedAt(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const pageRecord = await TenantSitePagesService.getByClientId(clientId, 'services');
      const merged = mergeServicesPageContent(pageRecord?.contentJson ?? null);

      setFormData(cloneServicesContent(merged));
      setHasSavedOverride(Boolean(pageRecord));
      setLastUpdatedAt(pageRecord?.updatedAt ?? null);
    } catch (error) {
      console.error('Erro ao carregar conteúdo da página de serviços:', error);
      toast.error('Não foi possível carregar o conteúdo da página de serviços.');
      setFormData(cloneServicesContent(defaultServicesPageContent));
      setHasSavedOverride(false);
      setLastUpdatedAt(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadContent();
  }, [clientId]);

  const updateSection = <
    TSection extends keyof TenantServicesPageContent,
    TField extends keyof NonNullable<TenantServicesPageContent[TSection]>,
  >(
    section: TSection,
    field: TField,
    value: NonNullable<TenantServicesPageContent[TSection]>[TField],
  ) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] ?? {}),
        [field]: value,
      },
    }));
  };

  const updateHeroAsset = (updater: (asset: TenantSiteAsset) => TenantSiteAsset) => {
    updateSection(
      'hero',
      'backgroundImage',
      updater((formData.hero?.backgroundImage as TenantSiteAsset) ?? createEmptyAsset()),
    );
  };

  const updateServiceItem = (
    serviceId: string,
    updater: (item: TenantSiteFeatureItem) => TenantSiteFeatureItem,
  ) => {
    const nextItems = [...serviceItems].map((item) =>
      item.id === serviceId ? updater(item) : item,
    );
    updateSection('services', 'items', nextItems);
  };

  const addServiceItem = () => {
    updateSection('services', 'items', [...serviceItems, createServiceItem()]);
  };

  const removeServiceItem = (serviceId: string) => {
    updateSection(
      'services',
      'items',
      serviceItems.filter((item) => item.id !== serviceId),
    );
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!clientId || !user?.userId) {
      toast.error('Selecione um tenant antes de salvar.');
      return;
    }

    setIsSaving(true);

    try {
      const saved = await TenantSitePagesService.upsertForClient(
        clientId,
        'services',
        formData,
        user.userId,
      );

      setFormData(cloneServicesContent(mergeServicesPageContent(saved.contentJson)));
      setHasSavedOverride(true);
      setLastUpdatedAt(saved.updatedAt);
      toast.success('Página de serviços atualizada com sucesso.');
    } catch (error) {
      console.error('Erro ao salvar página de serviços:', error);
      toast.error('Não foi possível salvar a página de serviços.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetToDefault = () => {
    setFormData(cloneServicesContent(defaultServicesPageContent));
  };

  const handleDeleteOverride = async () => {
    if (!clientId) {
      toast.error('Selecione um tenant antes de remover o override.');
      return;
    }

    try {
      setIsSaving(true);
      await TenantSitePagesService.deleteForClient(clientId, 'services');
      toast.success('As personalizações foram removidas e a versão padrão da página foi restaurada.');
      await loadContent();
    } catch (error) {
      console.error('Erro ao remover override da página de serviços:', error);
      toast.error('Não foi possível remover o override da página de serviços.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!selectedClient) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
        Selecione um tenant para editar a página de serviços.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="rounded-2xl bg-emerald-100 p-3 text-emerald-700">
              <Stethoscope className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Página de Serviços</h2>
              <p className="mt-1 max-w-3xl text-sm text-slate-600">
                Edite hero, bloco introdutório, cards de serviços e CTA final.
                A imagem do hero é enviada diretamente para o Firebase Storage.
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <p>
              <span className="font-semibold text-slate-800">Tenant:</span> {selectedClient.clientName}
            </p>
            <p>
              <span className="font-semibold text-slate-800">Origem atual:</span>{' '}
              {hasSavedOverride ? 'Conteúdo salvo no banco' : 'Fallback do sistema'}
            </p>
            <p>
              <span className="font-semibold text-slate-800">Última atualização:</span>{' '}
              {lastUpdatedAt ? new Date(lastUpdatedAt).toLocaleString('pt-BR') : 'Não salvo'}
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.3fr_0.7fr]">
        <form onSubmit={handleSave} className="space-y-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Hero</h3>
                <p className="text-sm text-slate-500">Título, subtítulo e imagem de destaque.</p>
              </div>
              <button
                type="button"
                onClick={() => void loadContent()}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                <RefreshCcw className="h-4 w-4" />
                Recarregar
              </button>
            </div>

            <div className="grid gap-4">
              <input
                type="text"
                value={formData.hero?.title ?? ''}
                onChange={(event) => updateSection('hero', 'title', event.target.value)}
                placeholder="Título principal"
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <textarea
                rows={4}
                value={formData.hero?.subtitle ?? ''}
                onChange={(event) => updateSection('hero', 'subtitle', event.target.value)}
                placeholder="Subtítulo"
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <TenantSiteImageUploadField
                  asset={formData.hero?.backgroundImage ?? null}
                  assetConfig={tenantSiteImageAssetConfigs.servicesHeroBackground}
                  assetPath="hero/background"
                  clientId={clientId}
                  label="Imagem de fundo do hero"
                  pageKey="services"
                  onUploaded={(asset) =>
                    updateHeroAsset((current) => ({
                      ...current,
                      ...asset,
                      alt: current.alt || asset.alt || '',
                    }))
                  }
                  onClear={() => updateHeroAsset(() => createEmptyAsset())}
                />

                <input
                  type="text"
                  value={formData.hero?.backgroundImage?.alt ?? ''}
                  onChange={(event) =>
                    updateHeroAsset((asset) => ({ ...asset, alt: event.target.value }))
                  }
                  placeholder="Texto alternativo"
                  className="mt-4 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900">Introdução</h3>
            <div className="mt-5 grid gap-4">
              <input
                type="text"
                value={formData.intro?.title ?? ''}
                onChange={(event) => updateSection('intro', 'title', event.target.value)}
                placeholder="Título do bloco"
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <textarea
                rows={4}
                value={formData.intro?.body ?? ''}
                onChange={(event) => updateSection('intro', 'body', event.target.value)}
                placeholder="Texto de introdução"
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Cards de serviços</h3>
                <p className="text-sm text-slate-500">Título da seção, subtítulo e lista de cards.</p>
              </div>
              <button
                type="button"
                onClick={addServiceItem}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                <ListPlus className="h-4 w-4" />
                Card
              </button>
            </div>

            <div className="space-y-4">
              <input
                type="text"
                value={formData.services?.title ?? ''}
                onChange={(event) => updateSection('services', 'title', event.target.value)}
                placeholder="Título da seção"
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <textarea
                rows={3}
                value={formData.services?.subtitle ?? ''}
                onChange={(event) => updateSection('services', 'subtitle', event.target.value)}
                placeholder="Subtítulo da seção"
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />

              <div className="space-y-4">
                {serviceItems.map((item) => (
                  <div key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <p className="font-medium text-slate-800">{item.title || 'Novo serviço'}</p>
                      <button
                        type="button"
                        onClick={() => removeServiceItem(item.id)}
                        className="rounded-xl border border-rose-200 bg-rose-50 p-2 text-rose-600 transition hover:bg-rose-100"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="grid gap-4 md:grid-cols-3">
                      <select
                        value={item.iconKey ?? 'stethoscope'}
                        onChange={(event) =>
                          updateServiceItem(item.id, (current) => ({
                            ...current,
                            iconKey: event.target.value,
                          }))
                        }
                        className="rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        {iconOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={item.title}
                        onChange={(event) =>
                          updateServiceItem(item.id, (current) => ({
                            ...current,
                            title: event.target.value,
                          }))
                        }
                        placeholder="Título"
                        className="md:col-span-2 rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                      <textarea
                        rows={3}
                        value={item.description}
                        onChange={(event) =>
                          updateServiceItem(item.id, (current) => ({
                            ...current,
                            description: event.target.value,
                          }))
                        }
                        placeholder="Descrição"
                        className="md:col-span-3 rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900">CTA final</h3>
            <div className="mt-5 grid gap-4">
              <input
                type="text"
                value={formData.cta?.title ?? ''}
                onChange={(event) => updateSection('cta', 'title', event.target.value)}
                placeholder="Título"
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <textarea
                rows={3}
                value={formData.cta?.body ?? ''}
                onChange={(event) => updateSection('cta', 'body', event.target.value)}
                placeholder="Texto"
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <div className="grid gap-4 md:grid-cols-2">
                <input
                  type="text"
                  value={formData.cta?.primaryCta?.label ?? ''}
                  onChange={(event) =>
                    updateSection('cta', 'primaryCta', {
                      ...(formData.cta?.primaryCta ?? { path: '/agendar', variant: 'primary' }),
                      label: event.target.value,
                    })
                  }
                  placeholder="Botão principal"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <input
                  type="text"
                  value={formData.cta?.primaryCta?.path ?? ''}
                  onChange={(event) =>
                    updateSection('cta', 'primaryCta', {
                      ...(formData.cta?.primaryCta ?? { label: 'Agendar Consulta', variant: 'primary' }),
                      path: event.target.value,
                    })
                  }
                  placeholder="/agendar"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={handleResetToDefault}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <RefreshCcw className="h-4 w-4" />
              Carregar padrão
            </button>

            <button
              type="submit"
              disabled={isSaving || isLoading}
              className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              {isSaving ? 'Salvando...' : 'Salvar página de serviços'}
            </button>
          </div>
        </form>

        <div className="space-y-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900">Resumo rápido</h3>
            <div className="mt-5 space-y-3 text-sm text-slate-600">
              <p>
                <span className="font-semibold text-slate-800">Hero:</span> {formData.hero?.title || 'Não preenchido'}
              </p>
              <p>
                <span className="font-semibold text-slate-800">Cards de serviços:</span> {serviceItems.length}
              </p>
              <p>
                <span className="font-semibold text-slate-800">CTA final:</span>{' '}
                {formData.cta?.title || 'Não preenchido'}
              </p>
            </div>
          </div>

          <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900 shadow-sm">
            <p className="font-semibold">Escopo desta etapa</p>
            <ul className="mt-3 list-disc space-y-2 pl-5">
              <li>Hero da página de serviços com imagem opcional.</li>
              <li>Bloco introdutório editável por tenant.</li>
              <li>Cards de serviços totalmente configuráveis.</li>
              <li>CTA final configurável por tenant.</li>
            </ul>
          </div>

          {hasSavedOverride && (
            <button
              type="button"
              onClick={() => void handleDeleteOverride()}
              disabled={isSaving}
              className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-3 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Trash2 className="h-4 w-4" />
              Remover override salvo
            </button>
          )}

          {isLoading && (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
              Carregando conteúdo da página de serviços...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServicesPageEditor;
